import payacaLogo from '@/assets/images/logo--payaca.svg';
import { AnchorButton, DividerWithLabel } from '@payaca/ambrosia';
import { FC, useMemo, type JSX } from 'react';
import payacaLogoHouse from './images/image--payaca-logo-home.png';
import payacaLogoTurbines from './images/image--payaca-logo-turbines.png';
import payacaLogoZaptec from './images/image--payaca-logo-zaptec.png';

import { LoginControl } from '@/ui/components';
import SocialAuthButtons from '@/ui/components/socialAuthButtons/SocialAuthButtons';

import { useParams } from 'react-router-dom';
import ForgotPasswordPage from '../forgotPasswordPage/ForgotPasswordPage';
import ResetPasswordPage from '../resetPasswordPage/ResetPasswordPage';
import FadeSlide from './FadeSlide';
import './LoginPage.sass';

const IMAGES = [payacaLogoHouse, payacaLogoZaptec, payacaLogoTurbines];

const LoginPage: FC = (): JSX.Element => {
  const { subPage, optionalToken } = useParams<{
    subPage?: string;
    optionalToken?: string;
  }>();
  const isForgotPassword = subPage === 'forgotPassword';
  // reset link and token
  const isResetPassword = subPage === 'reset';

  const randomImage = useMemo(() => {
    const index = Math.floor(Math.random() * IMAGES.length);
    return IMAGES[index];
  }, []);

  return (
    <div className="h-[100vh] w-full flex flex-col md:grid md:grid-cols-2">
      <div className="p-4 md:p-8 w-full md:h-full relative">
        <img
          src={payacaLogo}
          className="absolute left-4 top-4 h-[35px] md:h-[46px] md:top-8 md:left-8 z-20"
          alt="Payaca company logo"
        />

        <div className="hidden absolute left-0 top-0 w-full h-full md:flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-10 bg-[#F5F4FE]/50 backdrop-blur-xl pointer-events-none"></div>

          <div className="w-[204px] h-[204px] bg-[#FFCA2D]/50 rounded-full blur-xl [transform:translate(370px,-245px)]"></div>
          <div className="w-[270px] h-[270px] bg-[#0872ED]/50 rounded-full blur-xl [transform:translate(130px,-60px)]"></div>
          <div className="w-[174px] h-[174px] bg-[#7F56D9]/50 rounded-full blur-xl [transform:translate(-450px,280px)]"></div>
        </div>

        <div className="h-full relative hidden md:flex items-center justify-center z-10">
          <img
            src={randomImage}
            className={`max-w-[432px] w-full ${
              randomImage === payacaLogoZaptec ? 'mt-[80px]' : ''
            }`}
            alt="Various background images with Payaca logo"
          />
        </div>
      </div>
      <div className="flex-1 px-4 flex items-center justify-center">
        <div className="max-w-[400px] w-full">
          {!isForgotPassword && !isResetPassword ? (
            <FadeSlide key="login">
              <h1 className="mb-4 text-[32px] font-bold">Welcome back!</h1>

              <LoginControl />

              <div className="mt-4 flex flex-col gap-4">
                <div className="typography:prose">
                  <DividerWithLabel label="Or login with" />
                </div>
                <SocialAuthButtons isLogin={true} />

                <div className="flex flex-col items-center justify-center">
                  <AnchorButton
                    variant={'link-inline'}
                    href="https://payaca.com/try-payaca"
                  >
                    No account?
                  </AnchorButton>
                </div>
              </div>
            </FadeSlide>
          ) : isForgotPassword ? (
            <FadeSlide key="forgot-password">
              <ForgotPasswordPage />
            </FadeSlide>
          ) : (
            <FadeSlide key="reset-password">
              <ResetPasswordPage token={optionalToken} />
            </FadeSlide>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
